/**
 * The crowd: every character in the world, the local player included, drawn as an instance.
 *
 * ## Why
 *
 * The old path gave each character an `Object3D` tree — a `SkinnedMesh`, ~112 bone objects, an
 * `AnimationMixer` — and three walks the whole graph in `updateMatrixWorld` at the top of every
 * pass. Seventeen of them put an RTX 4080 at 11 fps with the GPU 36% busy and clocking *down*
 * (`docs/WEBGPU-NOTES.md` §6). A city built for three hundred players cannot pay per-character
 * CPU at all. So it does not: a character here is a **slot id and sixteen floats in a texture**.
 * Nothing about a character is an object in the scene.
 *
 * ## The shape of it
 *
 * - One `InstancedMesh` per body kind (two races), skinned on the GPU from a baked animation
 *   texture — bone matrices for every frame of every clip, written by
 *   `tools/assetgen/src/bake-anim.ts`. No `SkinnedMesh`, no `Skeleton`, no mixer.
 * - Visible gear — head, shoulders, chest overlay, weapon — is a separate `InstancedMesh` per
 *   look, drawn only for the characters wearing it, skinned from the *same* bone texture. Three
 *   heads, three shoulders, three chests and six weapons is fifteen meshes, and a character
 *   wearing none of them costs nothing in any of them.
 * - Every per-character value lives in one small `stateTexture`, indexed by slot: position,
 *   yaw, the two animation rows and the blend between them, scale, tint, hit flash. Body and
 *   gear read the same row, so a weapon follows its wearer's hand with no CPU work and no
 *   second copy of the animation state. That indirection is the whole gear design.
 * - Blob shadows are one more instanced mesh reading the same state texture. Characters cast no
 *   shadow maps at all.
 * - Nameplates are DOM, for the nearest thirty only, moved at 10 Hz with `translate3d`.
 *
 * Draw calls: 2 bodies + 15 gear + 1 blob shadow = **18, for any number of characters**. Three
 * hundred players and three cost the same. If a change here makes draws scale with the player
 * count, the change is wrong however fast it looks on one machine — see `docs/PERF-BUDGET.md`.
 *
 * ## What the CPU still does, once a frame
 *
 * Advance each active character's clip time, write its sixteen floats, frustum-test its bounding
 * sphere, and append its slot id to the dense instance list of the five meshes that draw it.
 * That is a few thousand array writes for a full city, and no allocation.
 */
import {
  BufferGeometry,
  Color,
  DataTexture,
  DynamicDrawUsage,
  Float32BufferAttribute,
  FloatType,
  DoubleSide,
  FrontSide,
  Frustum,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  MeshBasicNodeMaterial,
  NearestFilter,
  NoColorSpace,
  RGBAFormat,
  Sphere,
  Vector3,
  type Node,
  type PerspectiveCamera,
  type Scene,
} from 'three/webgpu';
import {
  Fn,
  attribute,
  cameraPosition,
  clamp,
  float,
  int,
  ivec2,
  mix,
  normalize,
  positionWorld,
  pow,
  saturate,
  textureLoad,
  uniform,
  varyingProperty,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import { AnimState, Race, packLook, type LookParts, type Vec3 } from '@stillsun/shared';
import './crowd.css';

// ---------------------------------------------------------------------------------------------
// The manifest — mirrored from tools/assetgen/src/bake-anim.ts. Keep the two in step.
// ---------------------------------------------------------------------------------------------

export type GearSlot = 'head' | 'shoulders' | 'chest' | 'weapon';

interface MeshSection {
  byteOffset: number;
  count: number;
  itemSize: number;
  type: 'f32' | 'u32';
}

interface PieceManifest {
  slot: 'body' | GearSlot;
  look: number;
  position: MeshSection;
  normal: MeshSection;
  uv: MeshSection;
  boneIndex: MeshSection;
  boneWeight: MeshSection;
  index: MeshSection;
  color?: MeshSection;
}

interface ClipEntry {
  name: string;
  row: number;
  frames: number;
  seconds: number;
  loop: boolean;
}

interface CrowdManifest {
  version: 1;
  body: string;
  family: string;
  fps: number;
  boneCount: number;
  boneNames: string[];
  influences: 1 | 2 | 3 | 4;
  sockets: Record<string, number>;
  texture: { file: string; width: number; height: number; channels: 4 };
  meshFile: string;
  height: number;
  clips: ClipEntry[];
  pieces: PieceManifest[];
}

// ---------------------------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------------------------

/**
 * `AnimState` (the `anim u8` on `PlayerSnap`) to a baked clip name, indexed by the wire value.
 *
 * The wire has twelve states and the bake has thirteen clips, so a few still share one: Downed
 * is Dead held at its last frame, and Fall borrows the run cycle for the reason below. Every
 * name here must exist in the bake — `tools/assetgen/src/bake-anim.ts` asserts it, because the
 * lookup below falls back to `clips[0]` and a typo would put the whole city into idle in
 * silence.
 */
const ANIM_CLIP: readonly string[] = [
  'idle', // Idle
  'walk', // Walk
  'run', // Run
  'jump', // Jump
  // Fall is the *descending* half of a jump, and `setState` restarts a one-shot whenever the
  // anim id changes — so 'jump' here would re-play the crouch and launch in mid-air at the
  // apex of every leap. The run cycle loops, keeps the legs moving, and never rewinds.
  'run', // Fall
  'sit', // Sit
  'attack1', // Attack
  'cast', // Cast
  'hit', // Hit
  'death', // Dead
  'death', // Downed
  'cheer', // Emote
];

/**
 * The bit layout of `PlayerSnap.look`, mirrored from `packLook` in `packages/shared`.
 *
 * The shared module owns the packing and this file never re-implements it — `setLook` takes the
 * wire word or the parts and hands the parts to `packLook`. What is duplicated here is only the
 * *read* side, because the per-frame loop unpacks four gear ids for every visible character and
 * building an object per character per frame is exactly the allocation this renderer exists to
 * avoid. If the shared layout moves, these two tables move with it.
 */
const LOOK_SHIFT = { race: 0, calling: 3, head: 6, shoulders: 10, chest: 14, weapon: 18 } as const;
const LOOK_MASK = {
  race: 0b111,
  calling: 0b111,
  head: 0b1111,
  shoulders: 0b1111,
  chest: 0b1111,
  weapon: 0b11111,
} as const;

/**
 * The bodies the game loads, in race order — the index the `race` field of a packed look holds.
 * Human is 0, Hollow is 1. Both are baked from the same skeleton family, so they share one bone
 * texture and one set of gear meshes.
 */
export const CROWD_BODIES: readonly string[] = ['human', 'hollow'];

export interface CrowdOptions {
  /** Body kinds to load, in race order. Names are directories under `assets/gen/crowd/`. */
  bodies: readonly string[];
  /** How many characters can exist at once. The city cap plus headroom. */
  maxSlots?: number;
  /** Stop drawing a character past this many metres. */
  drawDistance?: number;
  /** How many DOM nameplates to keep alive. */
  maxPlates?: number;
}

const DEFAULTS = {
  maxSlots: 512,
  drawDistance: 120,
  maxPlates: 30,
} as const;

/** Rows of the state texture. One texel each, `maxSlots` wide. */
const ROW_TRANSFORM = 0; // x, y, z, yaw
const ROW_ANIM = 1; // frame row A, frame row B, blend, scale
const ROW_TINT = 2; // tint r, g, b, hit flash
const STATE_ROWS = 3;

/**
 * The wash `setBroken` puts over a character whose kit is at 0 durability. Desaturated and a
 * little cool, dark enough to read against a lit body at thirty metres and light enough that a
 * night silhouette does not vanish. Not black: a ruined helm is ruined, not absent.
 */
const BROKEN_TINT: readonly [number, number, number] = [0.52, 0.5, 0.55];

/**
 * Texels per bone per frame in the bone texture: the three rows of a 3x4 matrix. The baker
 * writes this layout; `tools/assetgen/src/bake-anim.ts` documents why it is matrices and not
 * quaternions.
 */
const TEXELS_PER_BONE = 3;

/** Blob shadow: radius in metres at scale 1, how far off the ground it sits, and its darkest. */
const BLOB_RADIUS = 0.58;
const BLOB_LIFT = 0.02;
const BLOB_OPACITY = 0.55;

/** Nameplates are DOM: moving one is a style write, and 300 of those a frame is a jank budget. */
const PLATE_INTERVAL_MS = 100;
const PLATE_FADE_NEAR = 22;
const PLATE_FADE_FAR = 45;

/**
 * Radix for packing a slot id underneath a distance in one float. Must exceed `maxSlots`.
 */
const SLOT_PACK = 4096;

/** Bounding sphere used for the per-instance frustum test. */
const CULL_RADIUS = 1.3;
const CULL_CENTRE_Y = 0.95;

// ---------------------------------------------------------------------------------------------
// Per-slot bookkeeping. Plain parallel arrays: a character is an index, never an object.
// ---------------------------------------------------------------------------------------------

interface Body {
  name: string;
  manifest: CrowdManifest;
  mesh: InstancedMesh;
  slots: Float32Array;
  count: number;
  /** Clip row/frames per anim id, resolved once from the manifest's clip table. */
  clipRow: Int32Array;
  clipFrames: Int32Array;
  clipSeconds: Float32Array;
  clipLoop: Uint8Array;
}

interface Piece {
  slot: GearSlot;
  look: number;
  mesh: InstancedMesh;
  slots: Float32Array;
  count: number;
}

const SLOT_ORDER: readonly GearSlot[] = ['head', 'shoulders', 'chest', 'weapon'];

export class CrowdField {
  readonly scene: Scene;
  private readonly markerRoot: HTMLElement;
  private readonly maxSlots: number;
  private readonly drawDistance: number;
  private readonly maxPlates: number;

  /** The shared per-character state, read by every body, every gear piece and the shadows. */
  private readonly state: Float32Array;
  private readonly stateTex: DataTexture;

  private readonly bodies: Body[] = [];
  private readonly pieces: Piece[] = [];
  private blobs: InstancedMesh | null = null;
  private blobSlots: Float32Array;

  /** Slot allocation. `free` is a stack of reusable ids; `live` marks the ones in use. */
  private readonly free: number[] = [];
  private readonly live: Uint8Array;
  private readonly race: Uint8Array;
  private readonly animId: Uint8Array;
  private readonly animTime: Float32Array;
  private readonly animSpeed: Float32Array;
  private readonly flash: Float32Array;
  private readonly look: Uint32Array;
  /** 1 while SnapFlags.Broken is set on this character; see setBroken. */
  private readonly broken: Uint8Array;
  /** The tint setBroken displaced, so clearing it restores the caller's colour, not white. */
  private readonly baseTint: Float32Array;
  private readonly names: (string | null)[];
  private readonly plateOf: Int32Array;

  /** Dense list of live slots, rebuilt only when one is acquired or released. */
  private readonly liveList: number[] = [];

  private readonly plates: HTMLDivElement[] = [];
  /** The name span and the health fill inside each plate, so neither is ever queried. */
  private readonly plateName: HTMLSpanElement[] = [];
  private readonly plateFill: HTMLSpanElement[] = [];
  private readonly plateSlot: Int32Array;
  private lastPlateAt = 0;
  /**
   * Health fraction per slot, or -1 for "no bar". Only the selected target ever has one: a
   * nameplate bar over every body in a city is thirty style writes a frame for information
   * the player did not ask for.
   */
  private readonly plateHp: Float32Array;
  /** The one slot `setPlateHp` last lit, so lighting another puts the old one out. */
  private plateHpSlot = -1;

  private readonly frustum = new Frustum();
  private readonly frustumMatrix = new Matrix4();
  private readonly sphere = new Sphere(new Vector3(), CULL_RADIUS);
  private readonly scratch = new Vector3();

  /** Draw calls this field contributes on the last frame it updated. Read by the HUD. */
  drawCount = 0;
  /** Characters that survived culling on the last frame. */
  visibleCount = 0;

  private constructor(scene: Scene, markerRoot: HTMLElement, opts: CrowdOptions) {
    this.scene = scene;
    this.markerRoot = markerRoot;
    this.maxSlots = opts.maxSlots ?? DEFAULTS.maxSlots;
    this.drawDistance = opts.drawDistance ?? DEFAULTS.drawDistance;
    this.maxPlates = opts.maxPlates ?? DEFAULTS.maxPlates;

    const n = this.maxSlots;
    this.state = new Float32Array(n * STATE_ROWS * 4);
    this.stateTex = new DataTexture(this.state, n, STATE_ROWS, RGBAFormat, FloatType);
    this.stateTex.minFilter = NearestFilter;
    this.stateTex.magFilter = NearestFilter;
    this.stateTex.generateMipmaps = false;
    this.stateTex.colorSpace = NoColorSpace;
    this.stateTex.name = 'crowd-state';
    this.stateTex.needsUpdate = true;

    this.live = new Uint8Array(n);
    this.race = new Uint8Array(n);
    this.animId = new Uint8Array(n);
    this.animTime = new Float32Array(n);
    this.animSpeed = new Float32Array(n).fill(1);
    this.flash = new Float32Array(n);
    this.look = new Uint32Array(n);
    this.broken = new Uint8Array(n);
    this.baseTint = new Float32Array(n * 3).fill(1);
    this.names = new Array<string | null>(n).fill(null);
    this.plateOf = new Int32Array(n).fill(-1);
    this.plateHp = new Float32Array(n).fill(-1);
    this.blobSlots = new Float32Array(n);
    this.plateSlot = new Int32Array(this.maxPlates).fill(-1);
    for (let i = n - 1; i >= 0; i--) this.free.push(i);

    // Default tint is white, default scale 1 — a slot that is written before its look is is
    // still drawn in the right colours rather than black.
    for (let i = 0; i < n; i++) {
      const t = (ROW_TINT * n + i) * 4;
      this.state[t] = 1;
      this.state[t + 1] = 1;
      this.state[t + 2] = 1;
      this.state[(ROW_ANIM * n + i) * 4 + 3] = 1;
    }
  }

  /**
   * Load the baked bodies and build every mesh. One network round trip per body plus two binary
   * files each; nothing is built lazily, because a mesh built after the deploy precompile
   * compiles its pipeline on screen, as a freeze (`docs/WEBGPU-NOTES.md` §1).
   */
  static async create(
    scene: Scene,
    markerRoot: HTMLElement,
    opts: CrowdOptions,
  ): Promise<CrowdField> {
    const field = new CrowdField(scene, markerRoot, opts);
    await field.load(opts.bodies);
    return field;
  }

  private async load(bodies: readonly string[]): Promise<void> {
    const loaded: { manifest: CrowdManifest; mesh: ArrayBuffer; bones: Float32Array | null }[] = [];
    for (const [i, name] of bodies.entries()) {
      const dir = `/assets/gen/crowd/${name}/`;
      const manifest = (await (await fetch(`${dir}crowd.json`)).json()) as CrowdManifest;
      // Only the first body's bone texture is ever uploaded — everything in a family animates
      // from it (see below) — so the others' are not fetched at all. They are half a megabyte
      // each of float matrices, and downloading one to drop it on the floor is half a second of
      // a player's first load spent on nothing.
      const bones =
        i === 0
          ? new Float32Array(await (await fetch(dir + manifest.texture.file)).arrayBuffer())
          : null;
      const mesh = await (await fetch(dir + manifest.meshFile)).arrayBuffer();
      loaded.push({ manifest, mesh, bones });
    }
    const first = loaded[0];
    if (!first?.bones) throw new Error('crowd: no bodies to load');

    // One bone texture for the whole family. Bodies that share a skeleton share their gear and
    // their animation rows, which is what keeps fifteen gear meshes at fifteen rather than
    // fifteen per race. A body from another family is a second field, not a second texture here.
    for (const l of loaded) {
      if (l.manifest.family !== first.manifest.family) {
        console.warn(
          `[stillsun] crowd: ${l.manifest.body} is family "${l.manifest.family}", not ` +
            `"${first.manifest.family}" — it will animate from the wrong skeleton`,
        );
      }
    }
    const tex = first.manifest.texture;
    const boneTex = new DataTexture(first.bones, tex.width, tex.height, RGBAFormat, FloatType);
    boneTex.minFilter = NearestFilter;
    boneTex.magFilter = NearestFilter;
    boneTex.generateMipmaps = false;
    boneTex.colorSpace = NoColorSpace;
    boneTex.name = 'crowd-bones';
    boneTex.needsUpdate = true;
    this.boneTex = boneTex;

    const material = crowdMaterial(boneTex, this.stateTex, first.manifest);

    for (const l of loaded) {
      const piece = l.manifest.pieces.find((p) => p.slot === 'body');
      if (!piece) throw new Error(`crowd: ${l.manifest.body} has no body mesh`);
      const geometry = buildGeometry(l.mesh, piece);
      const mesh = this.makeInstanced(geometry, material, `crowd-body-${l.manifest.body}`);
      const clipRow = new Int32Array(ANIM_CLIP.length);
      const clipFrames = new Int32Array(ANIM_CLIP.length).fill(1);
      const clipSeconds = new Float32Array(ANIM_CLIP.length).fill(1);
      const clipLoop = new Uint8Array(ANIM_CLIP.length);
      ANIM_CLIP.forEach((wanted, id) => {
        const clip = l.manifest.clips.find((c) => c.name === wanted) ?? l.manifest.clips[0];
        if (!clip) return;
        clipRow[id] = clip.row;
        clipFrames[id] = clip.frames;
        clipSeconds[id] = clip.seconds;
        clipLoop[id] = clip.loop ? 1 : 0;
      });
      this.bodies.push({
        name: l.manifest.body,
        manifest: l.manifest,
        mesh,
        slots: new Float32Array(this.maxSlots),
        count: 0,
        clipRow,
        clipFrames,
        clipSeconds,
        clipLoop,
      });
    }

    // Gear comes from the first body's bake: the pieces are bound to the family's skeleton, not
    // to one race's mesh, so one set serves every body in the family.
    for (const p of first.manifest.pieces) {
      if (p.slot === 'body') continue;
      const geometry = buildGeometry(first.mesh, p);
      const mesh = this.makeInstanced(geometry, material, `crowd-${p.slot}-${String(p.look)}`);
      this.pieces.push({
        slot: p.slot,
        look: p.look,
        mesh,
        slots: new Float32Array(this.maxSlots),
        count: 0,
      });
    }

    this.blobs = this.makeInstanced(
      blobGeometry(),
      blobMaterial(this.stateTex),
      'crowd-blobshadows',
    );
    this.blobs.renderOrder = 1;

    for (let i = 0; i < this.maxPlates; i++) {
      const el = document.createElement('div');
      el.className = 'crowdplate off';
      // The name lives in its own span so the health strip under it survives a plate being
      // re-claimed by another slot — writing `textContent` on the plate itself would take the
      // bar with it. The strip stays `display: none` until `setPlateHp` writes a fraction, so
      // a street of thirty citizens still costs thirty plain plates.
      const name = document.createElement('span');
      name.className = 'cpn';
      const hp = document.createElement('span');
      hp.className = 'cphp';
      const fill = document.createElement('span');
      fill.className = 'cphpf';
      hp.append(fill);
      el.append(name, hp);
      this.markerRoot.appendChild(el);
      this.plates.push(el);
      this.plateName.push(name);
      this.plateFill.push(fill);
    }
  }

  private boneTex: DataTexture | null = null;

  private makeInstanced(
    geometry: BufferGeometry,
    material: MeshBasicNodeMaterial,
    name: string,
  ): InstancedMesh {
    const mesh = new InstancedMesh(geometry, material, this.maxSlots);
    mesh.name = name;
    mesh.count = 0;
    // Every transform lives in the state texture; the instance matrix is identity and never
    // written. It has to exist and be sane because three's own `instancedMesh()` node runs
    // before `positionNode` overrides the result, and a zeroed matrix would put NaNs through
    // `normalLocal` on the way past.
    const identity = new Matrix4();
    for (let i = 0; i < this.maxSlots; i++) mesh.setMatrixAt(i, identity);
    mesh.instanceMatrix.needsUpdate = true;
    // The CPU already did the culling, per instance, this frame. three's own test would only
    // repeat it against a bounding box that means nothing for a mesh whose vertices are all at
    // the origin until the vertex shader has run.
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    const slotAttr = new InstancedBufferAttribute(new Float32Array(this.maxSlots), 1);
    slotAttr.setUsage(DynamicDrawUsage);
    geometry.setAttribute('aSlot', slotAttr);
    this.scene.add(mesh);
    return mesh;
  }

  // -------------------------------------------------------------------------------------------
  // Slots
  // -------------------------------------------------------------------------------------------

  /** Take a slot. Returns -1 when the field is full — the caller draws nothing, and says so. */
  acquire(): number {
    const slot = this.free.pop();
    if (slot === undefined) return -1;
    this.live[slot] = 1;
    this.animId[slot] = AnimState.Idle;
    this.animTime[slot] = Math.random() * 3; // desync idles: a city of clones is worse than none
    this.animSpeed[slot] = 1;
    this.flash[slot] = 0;
    this.names[slot] = null;
    this.look[slot] = 0;
    this.race[slot] = 0;
    this.broken[slot] = 0;
    this.baseTint[slot * 3] = 1;
    this.baseTint[slot * 3 + 1] = 1;
    this.baseTint[slot * 3 + 2] = 1;
    // Every slot comes back in the state a fresh one is in. A slot that kept the last owner's
    // tint and scale is a monster's grey-green wash on the next player to take it, or a body at
    // scale 0 that never draws and cannot be debugged — both have to be impossible from here,
    // not remembered by four call sites.
    const t = (ROW_TINT * this.maxSlots + slot) * 4;
    this.state[t] = 1;
    this.state[t + 1] = 1;
    this.state[t + 2] = 1;
    this.state[t + 3] = 0;
    this.state[(ROW_ANIM * this.maxSlots + slot) * 4 + 3] = 1;
    this.liveList.push(slot);
    return slot;
  }

  release(slot: number): void {
    if (slot < 0 || slot >= this.maxSlots || this.live[slot] !== 1) return;
    this.live[slot] = 0;
    this.free.push(slot);
    const i = this.liveList.indexOf(slot);
    if (i >= 0) this.liveList.splice(i, 1);
    const plate = this.plateOf[slot] ?? -1;
    if (plate >= 0) {
      this.plates[plate]?.classList.add('off');
      this.plates[plate]?.classList.remove('hp');
      this.plateSlot[plate] = -1;
      this.plateOf[slot] = -1;
    }
    // A released slot is reused by somebody else within seconds: leaving a target's health
    // fraction on it would hang that bar over a stranger.
    this.plateHp[slot] = -1;
    if (this.plateHpSlot === slot) this.plateHpSlot = -1;
  }

  get count(): number {
    return this.liveList.length;
  }

  /**
   * The packed appearance word straight off `PlayerSnap.look`, or the parts it packs from.
   *
   * `Race` counts from 1 (`None = 0`) while the body list counts from 0, so an unset race draws
   * as the first body rather than as nothing — a character with no appearance yet is still a
   * person standing there.
   */
  setLook(slot: number, look: LookParts | number): void {
    if (this.live[slot] !== 1) return;
    const word = typeof look === 'number' ? look >>> 0 : packLook(look);
    this.look[slot] = word;
    const race = (word >>> LOOK_SHIFT.race) & LOOK_MASK.race;
    this.race[slot] = Math.min(this.bodies.length - 1, Math.max(0, race - 1));
  }

  setName(slot: number, name: string | null): void {
    if (this.live[slot] !== 1) return;
    this.names[slot] = name;
  }

  /**
   * Put a health bar on one slot's nameplate — the selected target's, and nobody else's.
   *
   * `frac` is 0..1; `null` takes the bar away. Exactly one slot can have one at a time, so
   * selecting a new target puts the old bar out without the caller having to remember who had
   * it. The bar itself is painted on the plate clock (10 Hz) with everything else.
   */
  setPlateHp(slot: number, frac: number | null): void {
    const target = frac === null || slot < 0 || this.live[slot] !== 1 ? -1 : slot;
    if (this.plateHpSlot >= 0 && this.plateHpSlot !== target) {
      const prev = this.plateHpSlot;
      this.plateHp[prev] = -1;
      const p = this.plateOf[prev] ?? -1;
      if (p >= 0) this.plates[p]?.classList.remove('hp');
    }
    this.plateHpSlot = target;
    if (target < 0) return;
    this.plateHp[target] = Math.max(0, Math.min(1, frac ?? 0));
  }

  setTint(slot: number, r: number, g: number, b: number): void {
    if (this.live[slot] !== 1) return;
    // While the broken wash is on, this channel is not ours to write: the wash holds the caller's
    // colour in `baseTint` and puts it back when the gear is repaired. Writing the live texels
    // here would be overwritten on the next frame anyway, and then *undone* by the restore — so
    // a monster's ash tint or a party colour set during the wash is stashed instead of lost.
    if (this.broken[slot] === 1) {
      this.baseTint[slot * 3] = r;
      this.baseTint[slot * 3 + 1] = g;
      this.baseTint[slot * 3 + 2] = b;
      return;
    }
    const o = (ROW_TINT * this.maxSlots + slot) * 4;
    this.state[o] = r;
    this.state[o + 1] = g;
    this.state[o + 2] = b;
  }

  /**
   * Ruined gear (`SnapFlags.Broken`): something this character is wearing is at 0 durability.
   *
   * It washes the whole body rather than the one piece, because the tint row is per character and
   * splitting it per gear mesh would cost a second state row for a state that is meant to read as
   * "this person's kit has given up", not as a materials puzzle. `BROKEN_TINT` is desaturated and
   * dark enough to read at nameplate distance without going black at night.
   *
   * It is a separate verb from `setTint` on purpose: the monster wash and the party-colour tint
   * both own that channel, and two callers writing the same four floats is the bug this avoids.
   */
  setBroken(slot: number, broken: boolean): void {
    if (this.live[slot] !== 1) return;
    if (this.broken[slot] === (broken ? 1 : 0)) return;
    this.broken[slot] = broken ? 1 : 0;
    const o = (ROW_TINT * this.maxSlots + slot) * 4;
    if (broken) {
      this.baseTint[slot * 3] = this.state[o] ?? 1;
      this.baseTint[slot * 3 + 1] = this.state[o + 1] ?? 1;
      this.baseTint[slot * 3 + 2] = this.state[o + 2] ?? 1;
      this.state[o] = BROKEN_TINT[0];
      this.state[o + 1] = BROKEN_TINT[1];
      this.state[o + 2] = BROKEN_TINT[2];
    } else {
      this.state[o] = this.baseTint[slot * 3] ?? 1;
      this.state[o + 1] = this.baseTint[slot * 3 + 1] ?? 1;
      this.state[o + 2] = this.baseTint[slot * 3 + 2] ?? 1;
    }
  }

  setScale(slot: number, scale: number): void {
    if (this.live[slot] !== 1) return;
    this.state[(ROW_ANIM * this.maxSlots + slot) * 4 + 3] = scale;
  }

  /**
   * The per-frame write. Position, facing and which clip is playing — everything a snapshot or
   * the local predictor has to say about a character.
   *
   * `animSpeed` scales clip time, so one walk cycle serves every speed between a stroll and a
   * sprint without a second clip or a blend tree.
   */
  setState(
    slot: number,
    pos: Vec3,
    yaw: number,
    anim: number,
    animSpeed = 1,
    restart = false,
  ): void {
    if (this.live[slot] !== 1) return;
    const o = (ROW_TRANSFORM * this.maxSlots + slot) * 4;
    this.state[o] = pos.x;
    this.state[o + 1] = pos.y;
    this.state[o + 2] = pos.z;
    this.state[o + 3] = yaw;
    const id = anim >= 0 && anim < ANIM_CLIP.length ? anim : AnimState.Idle;
    if (restart || this.animId[slot] !== id) {
      this.animId[slot] = id;
      // A looping clip picks up wherever the last one was, so a walk that becomes a run does not
      // snap a foot to the floor; a one-shot always starts at its first frame.
      const body = this.bodies[this.race[slot] ?? 0];
      if (restart || (body && body.clipLoop[id] !== 1)) this.animTime[slot] = 0;
    }
    this.animSpeed[slot] = animSpeed;
  }

  /** A hit: the body washes toward white and back over a fifth of a second. */
  hitFlash(slot: number): void {
    if (this.live[slot] !== 1) return;
    this.flash[slot] = 1;
  }

  /** Where a slot is, for the interact scan and the audio. Returns false for a dead slot. */
  positionOf(slot: number, out: Vector3): boolean {
    if (this.live[slot] !== 1) return false;
    const o = (ROW_TRANSFORM * this.maxSlots + slot) * 4;
    out.set(this.state[o] ?? 0, this.state[o + 1] ?? 0, this.state[o + 2] ?? 0);
    return true;
  }

  // -------------------------------------------------------------------------------------------
  // The frame
  // -------------------------------------------------------------------------------------------

  /**
   * One pass over the live characters: advance the clip, write the state texel, cull, and append
   * the slot to the dense instance list of every mesh that draws it. Then one upload each for
   * the state texture and the seventeen slot attributes.
   */
  update(
    dt: number,
    camera: PerspectiveCamera,
    viewer: Vec3,
    nowMs: number,
    screenW: number,
    screenH: number,
  ): void {
    const n = this.maxSlots;
    const state = this.state;

    for (const body of this.bodies) body.count = 0;
    for (const piece of this.pieces) piece.count = 0;
    let blobCount = 0;

    this.frustumMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.frustumMatrix);
    const maxDistSq = this.drawDistance * this.drawDistance;

    // Nameplates want the nearest few; gather distances in the same pass rather than a second
    // one, and only when a plate refresh is actually due.
    const platesDue = nowMs - this.lastPlateAt >= PLATE_INTERVAL_MS;
    const nearest = platesDue ? this.nearestScratch : null;
    let nearestCount = 0;

    for (const slot of this.liveList) {
      const raceIdx = this.race[slot] ?? 0;
      const body = this.bodies[raceIdx] ?? this.bodies[0];
      if (!body) continue;

      // --- clip time -> two texture rows and a blend --------------------------------------
      const id = this.animId[slot] ?? 0;
      const frames = body.clipFrames[id] ?? 1;
      const seconds = body.clipSeconds[id] ?? 1;
      const looping = body.clipLoop[id] === 1;
      let t = (this.animTime[slot] ?? 0) + dt * (this.animSpeed[slot] ?? 1);
      if (looping) t %= seconds;
      else if (t > seconds) t = seconds;
      this.animTime[slot] = t;
      const fpos = seconds > 0 ? (t / seconds) * (looping ? frames : frames - 1) : 0;
      const fa = Math.floor(fpos);
      const blend = fpos - fa;
      const base = body.clipRow[id] ?? 0;
      const rowA = base + (fa % frames);
      const rowB = base + (looping ? (fa + 1) % frames : Math.min(frames - 1, fa + 1));

      const ao = (ROW_ANIM * n + slot) * 4;
      state[ao] = rowA;
      state[ao + 1] = rowB;
      state[ao + 2] = blend;

      // --- hit flash -----------------------------------------------------------------------
      const f = this.flash[slot] ?? 0;
      if (f > 0) {
        const next = Math.max(0, f - dt * 5);
        this.flash[slot] = next;
        state[(ROW_TINT * n + slot) * 4 + 3] = next;
      }

      // --- cull ----------------------------------------------------------------------------
      const to = (ROW_TRANSFORM * n + slot) * 4;
      const x = state[to] ?? 0;
      const y = state[to + 1] ?? 0;
      const z = state[to + 2] ?? 0;
      const dx = x - viewer.x;
      const dy = y - viewer.y;
      const dz = z - viewer.z;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq > maxDistSq) continue;
      this.sphere.center.set(x, y + CULL_CENTRE_Y, z);
      if (!this.frustum.intersectsSphere(this.sphere)) continue;

      // --- draw lists ----------------------------------------------------------------------
      body.slots[body.count++] = slot;
      const word = this.look[slot] ?? 0;
      for (const gearSlot of SLOT_ORDER) {
        const look = (word >>> LOOK_SHIFT[gearSlot]) & LOOK_MASK[gearSlot];
        if (look === 0) continue; // bare, or unarmed
        const piece = this.pieceFor(gearSlot, look);
        if (piece) piece.slots[piece.count++] = slot;
      }
      this.blobSlots[blobCount++] = slot;

      if (nearest !== null && nearestCount < nearest.length) {
        // Distance and slot in one number, so the near set sorts without a second array and
        // without allocating a pair per character. The distance half must be an **integer**
        // before it is shifted, or the modulo below comes back with a fraction instead of a
        // slot id and only the handful of plates whose maths happened to land integral show up.
        // 1/64 m^2 is finer than any tie the eye could notice at these distances.
        nearest[nearestCount++] = Math.round(distSq * 64) * SLOT_PACK + slot;
      }
    }

    this.visibleCount = blobCount;

    // One upload for every per-character value in the world.
    this.stateTex.needsUpdate = true;

    let draws = 0;
    for (const body of this.bodies) draws += pushSlots(body.mesh, body.slots, body.count);
    for (const piece of this.pieces) draws += pushSlots(piece.mesh, piece.slots, piece.count);
    if (this.blobs) draws += pushSlots(this.blobs, this.blobSlots, blobCount);
    this.drawCount = draws;

    if (nearest !== null) {
      this.lastPlateAt = nowMs;
      this.updatePlates(nearest, nearestCount, camera, screenW, screenH);
    }
  }

  /** Scratch for the nameplate sort: distance and slot packed into one number, no allocation. */
  private readonly nearestScratch = new Float64Array(1024);

  private pieceFor(slot: GearSlot, look: number): Piece | null {
    for (const p of this.pieces) if (p.slot === slot && p.look === look) return p;
    return null;
  }

  /**
   * DOM plates for the nearest thirty, and none beyond. A plate is claimed by a slot and keeps
   * it while that slot stays in the near set, so the text is written on a change and never on a
   * frame that only moved somebody.
   */
  private updatePlates(
    packed: Float64Array,
    count: number,
    camera: PerspectiveCamera,
    screenW: number,
    screenH: number,
  ): void {
    const take = Math.min(count, this.maxPlates);
    // Partial selection sort: thirty passes over at most a few hundred, which beats sorting the
    // lot and is the only place in this file that would allocate if it used `Array.prototype`.
    for (let i = 0; i < take; i++) {
      let best = i;
      for (let j = i + 1; j < count; j++) {
        if ((packed[j] ?? Infinity) < (packed[best] ?? Infinity)) best = j;
      }
      const tmp = packed[i] ?? 0;
      packed[i] = packed[best] ?? 0;
      packed[best] = tmp;
    }

    const claimed = new Set<number>();
    for (let i = 0; i < take; i++) {
      const slot = ((packed[i] ?? 0) % SLOT_PACK) | 0;
      claimed.add(slot);
    }
    // Release plates whose slot has left the near set.
    for (let p = 0; p < this.maxPlates; p++) {
      const slot = this.plateSlot[p] ?? -1;
      if (slot >= 0 && !claimed.has(slot)) {
        this.plates[p]?.classList.add('off');
        this.plateSlot[p] = -1;
        this.plateOf[slot] = -1;
      }
    }

    const n = this.maxSlots;
    for (let i = 0; i < take; i++) {
      const slot = ((packed[i] ?? 0) % SLOT_PACK) | 0;
      const name = this.names[slot] ?? null;
      if (name === null || name === '') continue;
      let p = this.plateOf[slot] ?? -1;
      if (p < 0) {
        p = this.plateSlot.indexOf(-1);
        if (p < 0) continue;
        this.plateSlot[p] = slot;
        this.plateOf[slot] = p;
        // Into the name span, never the plate: `textContent` on the plate would delete the
        // health strip that lives beside it.
        const span = this.plateName[p];
        if (span) span.textContent = name;
      }
      const el = this.plates[p];
      if (!el) continue;

      // The selected target's health strip. One slot at a time (`setPlateHp`), repainted on
      // the plate clock rather than the frame clock — 10 Hz is a health bar, not an animation.
      const hpFrac = this.plateHp[slot] ?? -1;
      const wantsHp = hpFrac >= 0;
      if (wantsHp !== el.classList.contains('hp')) el.classList.toggle('hp', wantsHp);
      if (wantsHp) {
        const fill = this.plateFill[p];
        if (fill) fill.style.transform = `scaleX(${hpFrac.toFixed(3)})`;
      }

      const o = (ROW_TRANSFORM * n + slot) * 4;
      this.scratch.set(this.state[o] ?? 0, (this.state[o + 1] ?? 0) + 2.05, this.state[o + 2] ?? 0);
      // View space rather than an NDC z test: a point behind the eye divides by a negative w and
      // comes back mirrored (same reasoning as render/beacons.ts and game/npcs.ts).
      this.scratch.applyMatrix4(camera.matrixWorldInverse);
      if (this.scratch.z >= -camera.near) {
        el.classList.add('off');
        continue;
      }
      const dist = this.scratch.length();
      this.scratch.applyMatrix4(camera.projectionMatrix);
      // `applyMatrix4` on a Vector3 already divides by w.
      if (Math.abs(this.scratch.x) > 1.15 || Math.abs(this.scratch.y) > 1.15) {
        el.classList.add('off');
        continue;
      }
      const opacity =
        1 - Math.min(1, Math.max(0, (dist - PLATE_FADE_NEAR) / (PLATE_FADE_FAR - PLATE_FADE_NEAR)));
      if (opacity <= 0.02) {
        el.classList.add('off');
        continue;
      }
      el.classList.remove('off');
      // `translate3d` only: a transform is composited, `left`/`top` relayout the marker root.
      const px = Math.round((this.scratch.x * 0.5 + 0.5) * screenW);
      const py = Math.round((-this.scratch.y * 0.5 + 0.5) * screenH);
      el.style.transform = `translate3d(${String(px)}px, ${String(py)}px, 0)`;
      const q = Math.round(opacity * 20) / 20;
      if (q !== this.plateOpacity[p]) {
        this.plateOpacity[p] = q;
        el.style.opacity = String(q);
      }
    }
  }

  private readonly plateOpacity = new Float32Array(64).fill(-1);

  /**
   * Fill the field with `n` dummy citizens around a point — the `?crowd=N` gate in
   * `docs/PERF-BUDGET.md`. Returns the slots taken, so a caller can release them.
   */
  debugFill(n: number, around: Vec3, radius = 26): number[] {
    const taken: number[] = [];
    const pos = { x: 0, y: 0, z: 0 };
    const colour = new Color();
    const races = [Race.Human, Race.Hollow];
    // Every wearable look, cycled, so the gate exercises all fifteen gear meshes and not just
    // the two a random draw happened to pick.
    const anims = [AnimState.Idle, AnimState.Walk, AnimState.Run, AnimState.Sit, AnimState.Cast];
    for (let i = 0; i < n; i++) {
      const slot = this.acquire();
      if (slot < 0) break;
      taken.push(slot);
      // A phyllotactic spiral rather than a random scatter: repeatable between runs, which is the
      // whole point of a perf gate, and it fills the frustum evenly instead of clumping.
      const a = i * 2.399963;
      const r = radius * Math.sqrt((i + 1) / n);
      pos.x = around.x + Math.cos(a) * r;
      pos.y = around.y;
      pos.z = around.z + Math.sin(a) * r;
      this.setLook(slot, {
        race: races[i % races.length] ?? Race.Human,
        calling: (i % 2) + 1,
        head: i % 4,
        shoulders: (i >> 1) % 4,
        chest: (i >> 2) % 4,
        weapon: i % 7,
      });
      this.setName(slot, `Citizen ${String(i + 1)}`);
      colour.setHSL((i * 0.137) % 1, 0.42, 0.6);
      this.setTint(slot, colour.r, colour.g, colour.b);
      this.setState(slot, pos, a % (Math.PI * 2), anims[i % anims.length] ?? AnimState.Idle);
    }
    return taken;
  }

  clear(): void {
    for (const slot of [...this.liveList]) this.release(slot);
  }

  dispose(): void {
    this.clear();
    for (const body of this.bodies) {
      this.scene.remove(body.mesh);
      body.mesh.geometry.dispose();
      body.mesh.dispose();
    }
    for (const piece of this.pieces) {
      this.scene.remove(piece.mesh);
      piece.mesh.geometry.dispose();
      piece.mesh.dispose();
    }
    if (this.blobs) {
      this.scene.remove(this.blobs);
      this.blobs.geometry.dispose();
      this.blobs.dispose();
    }
    for (const el of this.plates) el.remove();
    this.plates.length = 0;
    this.stateTex.dispose();
    this.boneTex?.dispose();
  }
}

/** Copy a dense slot list into an instanced attribute and set the draw count. */
function pushSlots(mesh: InstancedMesh, slots: Float32Array, count: number): number {
  mesh.count = count;
  if (count === 0) return 0;
  const attr = mesh.geometry.getAttribute('aSlot') as InstancedBufferAttribute;
  const array = attr.array as Float32Array;
  array.set(slots.subarray(0, count));
  // Only the live prefix goes up the bus. The attribute is sized for the whole field, and a
  // city with four visible citizens must not upload two kilobytes of stale slot ids for them.
  attr.clearUpdateRanges();
  attr.addUpdateRange(0, count);
  attr.needsUpdate = true;
  return 1;
}

// ---------------------------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------------------------

function section(buffer: ArrayBuffer, s: MeshSection): Float32Array | Uint32Array {
  const len = s.count * s.itemSize;
  return s.type === 'u32'
    ? new Uint32Array(buffer, s.byteOffset, len)
    : new Float32Array(buffer, s.byteOffset, len);
}

function buildGeometry(buffer: ArrayBuffer, piece: PieceManifest): BufferGeometry {
  const geo = new BufferGeometry();
  geo.setAttribute(
    'position',
    new Float32BufferAttribute(section(buffer, piece.position) as Float32Array, 3),
  );
  geo.setAttribute(
    'normal',
    new Float32BufferAttribute(section(buffer, piece.normal) as Float32Array, 3),
  );
  geo.setAttribute('uv', new Float32BufferAttribute(section(buffer, piece.uv) as Float32Array, 2));
  geo.setAttribute(
    'aBoneIdx',
    new Float32BufferAttribute(section(buffer, piece.boneIndex) as Float32Array, 4),
  );
  geo.setAttribute(
    'aBoneW',
    new Float32BufferAttribute(section(buffer, piece.boneWeight) as Float32Array, 4),
  );
  if (piece.color) {
    geo.setAttribute(
      'aColor',
      new Float32BufferAttribute(section(buffer, piece.color) as Float32Array, 3),
    );
  }
  geo.setIndex(Array.from(section(buffer, piece.index) as Uint32Array));
  // Vertices live at the origin until the vertex shader has skinned them, so the computed
  // bounds mean nothing. Culling is per instance, on the CPU, in `update`.
  geo.boundingSphere = new Sphere(new Vector3(), 1e6);
  return geo;
}

/** A flat eight-sided disc for the blob shadows. Sixteen triangles for every character alive. */
function blobGeometry(): BufferGeometry {
  const geo = new BufferGeometry();
  const segments = 10;
  // Centre vertex, then one per segment. The rim closes on itself by index rather than by a
  // duplicated vertex at 2pi.
  const pos: number[] = [0, 0, 0];
  const uv: number[] = [0.5, 0.5];
  const index: number[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    pos.push(Math.cos(a), 0, Math.sin(a));
    uv.push(Math.cos(a) * 0.5 + 0.5, Math.sin(a) * 0.5 + 0.5);
  }
  for (let i = 0; i < segments; i++) {
    // Wound so the disc faces +y. Increasing angle in the XZ plane reads *clockwise* from above
    // — looking down -y flips the handedness — so the pair goes next-then-current, not the
    // other way round. Get this backwards and the whole field of shadows is simply not there.
    const cur = 1 + i;
    const next = 1 + ((i + 1) % segments);
    index.push(0, next, cur);
  }
  geo.setAttribute('position', new Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.boundingSphere = new Sphere(new Vector3(), 1e6);
  return geo;
}

// ---------------------------------------------------------------------------------------------
// The material: GPU skinning in TSL, and one stylised half-Lambert on top of it
// ---------------------------------------------------------------------------------------------

/**
 * Typed attribute helpers. `attribute(name, 'vec3')` infers `AttributeNode<string>` rather than
 * `AttributeNode<'vec3'>` — the generic widens the string literal — and an untyped node has no
 * swizzles, so every one of these spells the type parameter out.
 */
const attrFloat = (name: string): Node<'float'> => attribute<'float'>(name, 'float');
const attrVec2 = (name: string): Node<'vec2'> => attribute<'vec2'>(name, 'vec2');
const attrVec3 = (name: string): Node<'vec3'> => attribute<'vec3'>(name, 'vec3');
const attrVec4 = (name: string): Node<'vec4'> => attribute<'vec4'>(name, 'vec4');

/** `v[k]` for a vec4, unrolled in JavaScript because the bone loop is unrolled too. */
function comp(v: Node<'vec4'>, k: number): Node<'float'> {
  return k === 0 ? v.x : k === 1 ? v.y : k === 2 ? v.z : v.w;
}

/** World-space skinned normal, handed from the vertex stage to the fragment stage. */
const vCrowdNormal = varyingProperty('vec3', 'vCrowdNormal');
/** rgb tint, a = hit flash. */
const vCrowdTint = varyingProperty('vec4', 'vCrowdTint');

// Colours ride as `vec3` uniforms rather than `color` ones: every consumer below is vector
// maths, and a `color` node would have to be converted at each use for no gain.
const uSunDir = uniform(new Vector3(0.4, 0.75, 0.52));
const uSunColour = uniform(new Vector3(1.0, 0.94, 0.82));
const uAmbient = uniform(new Vector3(0.32, 0.36, 0.46));
const uRimColour = uniform(new Vector3(0.55, 0.72, 1.0));
const uRimStrength = uniform(0.35);

/**
 * Point the crowd's lighting at the sun. A uniform write and nothing else: the world clock moves
 * this every frame, and a shader rebuild sixty times a second is the bug this whole rewrite
 * exists to stop.
 */
export function setCrowdSun(dir: Vector3, sunColour: Color, ambient: Color, rim?: Color): void {
  uSunDir.value.copy(dir).normalize();
  uSunColour.value.set(sunColour.r, sunColour.g, sunColour.b);
  uAmbient.value.set(ambient.r, ambient.g, ambient.b);
  if (rim) uRimColour.value.set(rim.r, rim.g, rim.b);
}

/** How strong the rim light is. 0 turns it off entirely. */
export function setCrowdRim(strength: number): void {
  uRimStrength.value = strength;
}

function crowdMaterial(
  boneTex: DataTexture,
  stateTex: DataTexture,
  manifest: CrowdManifest,
): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();
  material.name = 'crowd';
  // Low-poly bodies are closed boxes; the back faces are never seen, and drawing them would
  // double the fragment count for three hundred characters.
  material.side = FrontSide;

  const influences = manifest.influences;

  const skin = Fn(() => {
    const slot = int(attrFloat('aSlot')).toVar();
    const transform = textureLoad(stateTex, ivec2(slot, int(ROW_TRANSFORM))).toVar();
    const anim = textureLoad(stateTex, ivec2(slot, int(ROW_ANIM))).toVar();
    const tint = textureLoad(stateTex, ivec2(slot, int(ROW_TINT))).toVar();

    const rowA = int(anim.x).toVar();
    const rowB = int(anim.y).toVar();
    const blend = anim.z.toVar();

    const boneIdx = attrVec4('aBoneIdx');
    const boneW = attrVec4('aBoneW');
    const p = vec4(attrVec3('position'), float(1)).toVar();
    const nrm = attrVec3('normal');

    const acc = vec3(0, 0, 0).toVar();
    const nacc = vec3(0, 0, 0).toVar();
    for (let k = 0; k < influences; k++) {
      const b = int(comp(boneIdx, k)).mul(int(TEXELS_PER_BONE)).toVar();
      // Frame N and frame N+1, mixed. A component-wise lerp of two 3x4 matrix rows at 30 Hz is
      // indistinguishable from a proper interpolation and needs no normalisation step, which a
      // sampled quaternion pair would.
      const r0 = mix(
        textureLoad(boneTex, ivec2(b, rowA)),
        textureLoad(boneTex, ivec2(b, rowB)),
        blend,
      ).toVar();
      const r1 = mix(
        textureLoad(boneTex, ivec2(b.add(int(1)), rowA)),
        textureLoad(boneTex, ivec2(b.add(int(1)), rowB)),
        blend,
      ).toVar();
      const r2 = mix(
        textureLoad(boneTex, ivec2(b.add(int(2)), rowA)),
        textureLoad(boneTex, ivec2(b.add(int(2)), rowB)),
        blend,
      ).toVar();
      const w = comp(boneW, k);
      acc.addAssign(vec3(r0.dot(p), r1.dot(p), r2.dot(p)).mul(w));
      nacc.addAssign(vec3(r0.xyz.dot(nrm), r1.xyz.dot(nrm), r2.xyz.dot(nrm)).mul(w));
    }

    // Instance transform: uniform scale, yaw about +y, then translate. Yaw 0 faces -z, which is
    // the sim's convention, and the bake already turned the body to match.
    const scale = anim.w.toVar();
    const yaw = transform.w.toVar();
    const cs = yaw.cos().toVar();
    const sn = yaw.sin().toVar();
    const local = acc.mul(scale).toVar();

    vCrowdNormal.assign(
      normalize(
        vec3(nacc.x.mul(cs).add(nacc.z.mul(sn)), nacc.y, nacc.z.mul(cs).sub(nacc.x.mul(sn))),
      ),
    );
    vCrowdTint.assign(tint);

    return vec3(
      local.x.mul(cs).add(local.z.mul(sn)).add(transform.x),
      local.y.add(transform.y),
      local.z.mul(cs).sub(local.x.mul(sn)).add(transform.z),
    );
  });

  material.positionNode = skin();

  const shade = Fn(() => {
    const n = normalize(vCrowdNormal).toVar();
    // Half-Lambert: the wrap keeps a low-poly silhouette readable from the shadow side, which a
    // clamped N.L does not, and it is why this needs no fill light.
    const lambert = n.dot(uSunDir).mul(0.5).add(0.5).toVar();
    const lit = mix(uAmbient, uSunColour, lambert.mul(lambert)).toVar();

    const colour = attrVec3('aColor').mul(vCrowdTint.xyz).mul(lit).toVar();

    // Rim: a thin cool edge that separates a body from the wall behind it. Free at this poly
    // count and the biggest readability win at three hundred characters deep in a crowd.
    const view = normalize(cameraPosition.sub(positionWorld)).toVar();
    const rim = pow(saturate(float(1).sub(saturate(n.dot(view)))), float(3)).mul(uRimStrength);
    colour.addAssign(uRimColour.mul(rim));

    // A hit washes the body toward white and back over a fifth of a second.
    return vec4(mix(colour, vec3(1.0, 0.86, 0.78), clamp(vCrowdTint.w, 0, 1)), 1);
  });
  material.colorNode = shade();

  return material;
}

/**
 * Blob shadows: one instanced disc per character, laid on the ground under it, read from the
 * same state texture. Characters cast no shadow map at all — one sun cascade covers the world
 * and nothing else — so this is the only thing that grounds a body, and it has to be cheap.
 */
function blobMaterial(stateTex: DataTexture): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();
  material.name = 'crowd-blob';
  material.transparent = true;
  material.depthWrite = false;
  material.polygonOffset = true;
  material.polygonOffsetFactor = -2;
  // A flat disc a couple of centimetres off the ground: a camera that dips below the feet must
  // still see it rather than watch every shadow in the city blink out.
  material.side = DoubleSide;
  // …but in ONE pass. A transparent `DoubleSide` material defaults to two draws — a BackSide
  // pipeline and a FrontSide one — so the blob field asks the renderer for two pipeline keys
  // over the same pair of shader programs, and both of them show up as fresh compiles the first
  // time a crowd is drawn in a map. There is nothing here for the two passes to get right: the
  // disc is flat, uniformly black, has `depthWrite` off, and has no back-to-front ordering
  // within itself to preserve. Measured at a gate crossing (2026-09-06): two `crowd-blobshadows`
  // entries in the new-pipeline list that compiled no vertex or fragment program at all.
  material.forceSinglePass = true;

  const place = Fn(() => {
    const slot = int(attrFloat('aSlot')).toVar();
    const transform = textureLoad(stateTex, ivec2(slot, int(ROW_TRANSFORM))).toVar();
    const anim = textureLoad(stateTex, ivec2(slot, int(ROW_ANIM))).toVar();
    const r = float(BLOB_RADIUS).mul(anim.w).toVar();
    const p = attrVec3('position');
    return vec3(
      p.x.mul(r).add(transform.x),
      transform.y.add(float(BLOB_LIFT)),
      p.z.mul(r).add(transform.z),
    );
  });
  material.positionNode = place();

  const shade = Fn(() => {
    const d = attrVec2('uv').sub(vec2(0.5, 0.5)).length().mul(2);
    const a = saturate(float(1).sub(d)).toVar();
    return vec4(0, 0, 0, a.mul(a).mul(BLOB_OPACITY));
  });
  material.colorNode = shade();

  return material;
}
